/* fy-buy-box — seletor de variantes em cards + form de compra.

   Responsabilidades:
   - manter o input hidden name="id" atualizado (com `change` propagado, que é
     como apps/widgets percebem a troca de variante);
   - refletir no botão o preço correto: preço da assinatura quando o plano
     "Subscribe & Save" está ativo no widget do Loop, preço cheio no one-time;
   - enviar ao carrinho via fetch e seguir direto para o checkout, levando o
     selling_plan escolhido no widget;
   - manter ?variant= na URL. */
(() => {
  if (customElements.get('fy-buy-box')) return;

  const LOOP_OPTION = '.loop-widget-purchase-option';
  const LOOP_ONETIME = 'loop-widget-purchase-option-onetime';
  const LOOP_SELECTED = 'loop-widget-purchase-option-selected';

  class FyBuyBox extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-fy-variant-input]');
      this.submitButton = this.querySelector('[data-fy-atc]');
      this.atcLabel = this.querySelector('[data-fy-atc-label]');
      this.atcPrice = this.querySelector('[data-fy-atc-price]');
      /* o preço vive dentro de um wrapper que também contém o separador "·";
         é o wrapper que deve ser escondido, senão o separador fica órfão */
      this.atcPriceWrapper = this.atcPrice?.closest('.fy-buy-box__atc-price') || this.atcPrice;
      this.errorEl = this.querySelector('[data-fy-error]');
      this.radios = Array.from(this.querySelectorAll('[data-fy-variant-radio]'));

      /* O form e o retorno do checkout valem mesmo sem seletor de variantes
         (produto de variante única) — por isso são registrados antes de
         qualquer saída antecipada. */
      if (this.form) {
        this.onFormSubmit = (event) => this.onSubmit(event);
        this.form.addEventListener('submit', this.onFormSubmit);
      }

      /* Ao voltar do checkout o navegador pode restaurar a página do bfcache
         com o DOM exatamente como ficou — inclusive o botão em estado de
         carregando e desabilitado. Reabilita nesse retorno. */
      this.onPageShow = (event) => {
        if (event.persisted) this.resetSubmitState();
      };
      window.addEventListener('pageshow', this.onPageShow);

      /* Link "90-Day Money-Back Guarantee": rola suave até a seção de
         garantia. Se o alvo não existir (seção removida) ou sem JS, o href
         âncora nativo segue como fallback. */
      const guaranteeLink = this.querySelector('[data-fy-guarantee-link]');
      if (guaranteeLink) {
        guaranteeLink.addEventListener('click', (event) => {
          const id = (guaranteeLink.getAttribute('href') || '').replace('#', '');
          const anchor = id ? document.getElementById(id) : null;
          if (!anchor) return;
          event.preventDefault();
          /* a âncora é um div vazio no topo da seção; centralizar a seção
             inteira (curta) posiciona melhor que centralizar o div */
          const target = anchor.closest('[data-section-type]') || anchor;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      this.watchSubscriptionWidget();

      if (!this.radios.length) return;

      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => this.onVariantChange(radio));
      });

      const checked = this.radios.find((radio) => radio.checked);
      if (checked) this.onVariantChange(checked, true);
    }

    /* No editor de tema a seção é re-renderizada várias vezes; sem esta
       limpeza os listeners de document/window se acumulariam a cada render. */
    disconnectedCallback() {
      if (this.widgetObserver) this.widgetObserver.disconnect();
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      if (this.onWidgetClick) document.removeEventListener('click', this.onWidgetClick);
      if (this.onFormSubmit && this.form) {
        this.form.removeEventListener('submit', this.onFormSubmit);
      }
    }

    get checkedRadio() {
      return this.radios.find((radio) => radio.checked);
    }

    /* --- widget de assinatura (app Loop) ---------------------------------- */

    /* Fonte de verdade: o input selling_plan que o app mantém no formulário —
       é exatamente o que vai para o carrinho. A classe `selected` do widget é
       só fallback, porque nem sempre acompanha o clique de imediato. */
    isSubscriptionSelected() {
      const sellingPlan = this.getSellingPlanInput();
      if (sellingPlan) return Boolean(sellingPlan.value);
      const selected = document.querySelector(`${LOOP_OPTION}.${LOOP_SELECTED}`);
      return selected ? !selected.classList.contains(LOOP_ONETIME) : false;
    }

    getSellingPlanInput() {
      return (
        this.form?.querySelector('input[name="selling_plan"]') ||
        document.querySelector('input[name="selling_plan"]')
      );
    }

    /* O app renderiza e re-renderiza o widget por conta própria, podendo trocar
       os elementos internos. Por isso observamos o NOSSO container (que nunca é
       recriado) e usamos delegação de clique no documento — assim nada fica
       órfão quando o widget se redesenha.
       O callback só escreve no botão, fora do widget, então não realimenta
       mutações — diferente dos scripts legados que congelavam a página. */
    watchSubscriptionWidget() {
      const host = this.querySelector('.fy-buy-box__app-blocks') || this;

      this.widgetObserver = new MutationObserver(() => this.refreshPrice());
      this.widgetObserver.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'value']
      });

      /* o clique no card só atualiza o input depois do próprio JS do app;
         reavaliamos algumas vezes para não depender do tempo dele */
      this.onWidgetClick = (event) => {
        if (!event.target.closest(LOOP_OPTION)) return;
        [60, 200, 500, 900].forEach((delay) => {
          setTimeout(() => this.refreshPrice(), delay);
        });
      };
      document.addEventListener('click', this.onWidgetClick);

      this.refreshPrice();
    }

    /* --- estado do botão --------------------------------------------------- */

    /* O texto do botão pode ser reescrito por apps da loja (teste A/B de CTA,
       por exemplo). Guardamos o texto vigente e só o trocamos por "Sold Out"
       quando necessário, restaurando depois — assim não desfazemos o que o app
       definiu nem deixamos o rótulo oscilando a cada troca de variante. */
    updateLabel(available) {
      if (!this.atcLabel) return;
      const soldOutText = this.atcLabel.dataset.textSoldout || '';
      const current = this.atcLabel.textContent.trim();

      if (current && current !== soldOutText) this.availableLabel = current;

      const nextText = available
        ? this.availableLabel || this.atcLabel.dataset.textAdd || ''
        : soldOutText || this.availableLabel || '';

      if (nextText && nextText !== current) this.atcLabel.textContent = nextText;
    }

    refreshPrice() {
      const radio = this.checkedRadio;
      if (!radio) return;
      this.syncLoopCompareAt(radio);
      if (!this.atcPrice) return;
      const price = this.isSubscriptionSelected()
        ? radio.dataset.subscriptionPrice || radio.dataset.variantPrice
        : radio.dataset.variantPrice;
      if (price && this.atcPrice.textContent.trim() !== price) {
        this.atcPrice.textContent = price;
      }
    }

    /* O widget do Loop risca o preço da variante (o bundle já com desconto);
       a âncora certa é o preço cheio sem bundle — o compare_at_price da
       variante, que este build do widget ignora (flag interna desligada).
       Reescrevemos o texto com o valor oficial da Shopify vindo do Liquid.
       Só escreve quando difere, então a mutação resultante não realimenta o
       observer em loop. O badge "Save X%" acompanha via atributo lido pelo
       CSS (content: attr) — atributo fora do attributeFilter do observer. */
    syncLoopCompareAt(radio) {
      const host = this.querySelector('.fy-buy-box__app-blocks');
      if (!host) return;
      const subscriptionOption =
        '.loop-widget-purchase-option:not(.loop-widget-purchase-option-onetime)';

      const compare = radio.dataset.comparePrice;
      const compareEl = host.querySelector(
        `${subscriptionOption} .loop-widget-purchase-option-compare-at-price`
      );
      if (compare && compareEl && compareEl.textContent.trim() !== compare) {
        compareEl.textContent = compare;
      }

      const percent = radio.dataset.savePercent;
      const priceContainer = host.querySelector(
        `${subscriptionOption} .loop-widget-purchase-option-price-each-container`
      );
      if (priceContainer) {
        /* desconto zerado ou negativo: atributo vazio, que o CSS renderiza
           como badge nenhum (e derruba o fallback estático 'Save 15%') */
        const saveText = Number(percent) > 0 ? `Save ${percent}%` : '';
        if (priceContainer.getAttribute('data-fy-save') !== saveText) {
          priceContainer.setAttribute('data-fy-save', saveText);
        }
      }

      /* One-time: o Loop sempre marca este riscado como oculto, e o CSS do
         widget (admin do Loop) o força visível para exibir a âncora. Quando a
         variante não tem compare_at (1 pouch), o Loop renderiza "$0.00" —
         limpamos o texto em vez de esconder o elemento (esconder preço via
         CSS já provou ser perigoso com scripts de tema). */
      const onetimeCompare = host.querySelector(
        '.loop-widget-purchase-option-onetime .loop-widget-purchase-option-compare-at-price'
      );
      if (onetimeCompare) {
        const hasAnchor = compare && compare !== radio.dataset.variantPrice;
        const anchorText = hasAnchor ? compare : '';
        if (onetimeCompare.textContent.trim() !== anchorText) {
          onetimeCompare.textContent = anchorText;
        }
      }
    }

    /* Textos do widget que espelham a variante selecionada: o selo do card de
       assinatura ("Most Popular"/"Best Value", texto e cor) e o subtítulo do
       card one-time ("2 pouches · 60 servings"). Escreve como CSS custom
       properties no NOSSO wrapper (que o Loop nunca recria), e os
       pseudo-elementos leem via content: var() — herda, então sobrevive a
       qualquer re-render do widget sem precisar reescrever nada. */
    syncPlanBadge(radio) {
      const host = this.querySelector('.fy-buy-box__app-blocks');
      if (!host) return;
      const text = (radio.dataset.badge || '').trim();
      host.classList.toggle('fy-no-plan-badge', !text);
      if (text) {
        host.style.setProperty('--fy-loop-badge-text', JSON.stringify(text));
        /* sem cor configurada: REMOVE a variável (variável vazia é "setada"
           e anularia o fallback do var(), deixando o fundo transparente) */
        const color = (radio.dataset.badgeColor || '').trim();
        if (color) {
          host.style.setProperty('--fy-loop-badge-bg', color);
        } else {
          host.style.removeProperty('--fy-loop-badge-bg');
        }
      }

      /* Subtítulo do card one-time ("2 pouches · 60 servings"): mesmo
         mecanismo do selo. Sem texto (metafield de dias ausente), esconde
         em vez de deixar o fallback estático mentir. */
      const onetimeSub = (radio.dataset.onetimeLabel || '').trim();
      host.classList.toggle('fy-no-onetime-sub', !onetimeSub);
      if (onetimeSub) {
        host.style.setProperty('--fy-loop-onetime-sub', JSON.stringify(onetimeSub));
      }
    }

    onVariantChange(radio, initial = false) {
      if (this.variantInput) {
        this.variantInput.value = radio.value;
        if (!initial) {
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.syncPlanBadge(radio);

      const available = radio.dataset.available !== 'false';
      if (this.submitButton) this.submitButton.disabled = !available;
      this.updateLabel(available);
      if (this.atcPriceWrapper) this.atcPriceWrapper.hidden = !available;
      if (this.atcPrice) this.refreshPrice();

      /* avisa outras seções (ex.: barra de CTA fixa) sobre a variante ativa */
      document.dispatchEvent(
        new CustomEvent('fy:variant-change', {
          detail: {
            variantId: radio.value,
            available,
            price: radio.dataset.variantPrice,
            subscriptionPrice: radio.dataset.subscriptionPrice,
            comparePrice: radio.dataset.comparePrice
          }
        })
      );

      if (!initial) this.updateUrl(radio.value);
    }

    /* --- compra ------------------------------------------------------------ */

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitting || !this.form) return;
      this.submitting = true;
      this.setLoading(true);
      this.showError('');

      const formData = new FormData(this.form);

      /* garante o plano de assinatura mesmo se o app injetar o input fora
         do nosso formulário */
      if (!formData.get('selling_plan')) {
        const sellingPlan = this.getSellingPlanInput();
        if (sellingPlan?.value && this.isSubscriptionSelected()) {
          formData.set('selling_plan', sellingPlan.value);
        }
      }
      if (!this.isSubscriptionSelected()) formData.delete('selling_plan');

      let response;
      try {
        /* Checkout sempre com exatamente o que foi selecionado: esvaziamos o
           carrinho antes de adicionar. Sem isso, voltar do checkout e comprar
           de novo acumularia unidades — e esta página não expõe carrinho, então
           o cliente não teria como corrigir.
           (O "Buy it now" nativo isolaria o checkout, mas a documentação da
           Shopify diz que cart permalinks/checkout direto não funcionam com
           selling plans, e ele pularia o /cart/add — que é o gatilho dos
           brindes da assinatura.) */
        await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
      } catch (error) {
        /* falha de rede: cai para a submissão normal, que leva ao carrinho —
           o cliente nunca fica com um botão que não faz nada */
        console.error('fy-buy-box: rede indisponível ao adicionar ao carrinho', error);
        this.form.submit();
        return;
      }

      if (response.ok) {
        window.location.href = '/checkout';
        return;
      }

      /* erro de validação da Shopify (estoque, limite por pedido, etc.):
         mostra a mensagem real e devolve o botão, sem recarregar a página */
      let message = '';
      try {
        const data = await response.json();
        message = data.description || data.message || '';
      } catch (error) {
        message = '';
      }
      console.error('fy-buy-box: cart/add retornou', response.status, message);
      this.showError(message || this.errorEl?.dataset.fallback || 'Something went wrong. Please try again.');
      this.resetSubmitState();
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', loading);
      this.submitButton.disabled = loading;
    }

    /* devolve o botão ao estado real: habilitado, a menos que a variante
       selecionada esteja indisponível */
    resetSubmitState() {
      this.submitting = false;
      if (!this.submitButton) return;
      this.submitButton.classList.remove('is-loading');
      const radio = this.checkedRadio;
      const available = radio ? radio.dataset.available !== 'false' : true;
      this.submitButton.disabled = !available;
      this.updateLabel(available);
      this.refreshPrice();
    }

    updateUrl(variantId) {
      if (!window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }
  }

  customElements.define('fy-buy-box', FyBuyBox);
})();
